import * as React from "react";
import { css } from "@emotion/core";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  ButtonGroup,
  Navbar,
  NavbarGroup,
  Alignment,
  Intent,
  Popover,
  PopoverInteractionKind,
  PopoverPosition,
} from "@blueprintjs/core";
import { format, utcToZonedTime } from "date-fns-tz";
import { OrderStatus, OrdersTable } from "components/common";
import {
  useSinkState,
  useSinkActions,
  useCheckEditAccess,
  useToastActions,
} from "context/";
import {
  CheckEditAccess,
  CheckViewAccess,
} from "context/";
import { useCountdown } from "components/Orders/hooks";
import { formatCountdown } from "utils/format";
import { useDownloads } from "../../hooks/useDownloads";
import ApiOrders from "api/orders";
import ApiSurveys from "api/surveys";
import { DownloadSelector } from "components/common/DownloadSelector";
import {
  confirmLockSurvey,
  confirmUnlockSurvey,
} from "components/common/surveyLock";
import { isSurveyArchived } from "interfaces";

const Orders = ({ status, orders, isArchived }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [cancellingOrder, setCancellingOrder] = React.useState(false);

  const ordersForSurvey = orders.filter(
    (order) => order.survey_id === Number(id)
  );

  const order = ordersForSurvey[0] || null;

  const remainingSeconds = useCountdown(
    order ? new Date(order.scheduled_at) : null
  );

  const handleCancelOrder = async () => {
    if (!order || !order.id) return;

    setCancellingOrder(true);
    try {
      await ApiOrders.cancelScheduledOrder(Number(id), order.id);
      navigate(`/surveys/${id}/orders/${order.id}`);
    } catch (error) {
      console.error("Error cancelling order:", error);
      setCancellingOrder(false);
    }
  };

  return (
    <>
      Order Status
      <span
        css={css`
          margin: 0 1rem;
        `}
      >
        {ordersForSurvey.length > 0 ? (
          <Popover
            interactionKind={PopoverInteractionKind.HOVER}
            position={PopoverPosition.TOP_LEFT}
            popoverClassName="bp6-dark"
            content={
              <div
                css={css`
                  padding: 0.75rem 1rem;
                `}
              >
                <OrdersTable orders={ordersForSurvey} />
              </div>
            }
          >
            <span tabIndex={0} aria-label="Order details">
              <OrderStatus status={status || ""} />
            </span>
          </Popover>
        ) : (
          <OrderStatus status={status || ""} />
        )}
      </span>
      {!isArchived && (
        <ButtonGroup>
          {order === null && (
            <Button
              icon="cube-add"
              text="Create Order"
              intent="success"
              onClick={() => navigate(`/surveys/${id}/orders/new`)}
            />
          )}
          {order && order.scheduled_at && remainingSeconds > 0 && (
            <Button
              icon="cube-remove"
              text={`Cancel Order (${formatCountdown(remainingSeconds)})`}
              intent="danger"
              loading={cancellingOrder}
              onClick={handleCancelOrder}
            />
          )}
          {order && order.scheduled_at === null && order.submitted === null && (
            <Button
              icon="cube-add"
              text="Continue Order"
              intent="success"
              onClick={() => navigate(`/surveys/${id}/orders/${order.id}`)}
            />
          )}
        </ButtonGroup>
      )}
    </>
  );
};


const Downloads = () => {
  const navigate = useNavigate();

  const { id } = useParams();

  const [showLockedPopup, setShowLockedPopup] = React.useState(false);
  const [showDownloadsPopup, setShowDownloadsPopup] = React.useState(false);
  const [lockingInProgress, setLockingInProgress] = React.useState(false);

  const { sink } = useSinkState();
  const { resink } = useSinkActions();
  const { showError } = useToastActions();
  const currentSurvey = React.useMemo(
    () => sink.surveys.find((survey) => survey.id === Number(id)),
    [sink.surveys, id]
  );

  const { downloads, isLoading } = useDownloads(Number(id));

  const hasEditAccess = useCheckEditAccess(currentSurvey);

  const locked =
    (currentSurvey &&
      currentSurvey.locked_by_id !== null &&
      currentSurvey.locked_time !== null) ||
    false;

  const downloadsForSurvey = downloads || [];

  /*
     below we check to see if the survey has a flight
     this is because surveys that don't have orders
     don't have flight dates. we currently don't need
     reports if the survey doesn't have order data
     remove this when reports no longer have
     survey dependency
  */
  const showLock = currentSurvey?.nominal_flight_date;
  const generateReport = sink.organization.generate_xls;
  const isArchived = isSurveyArchived(currentSurvey);

  const lockedOnButton = (
    <Button
      icon="lock"
      text={`Locked on ${format(
        utcToZonedTime(
          new Date(currentSurvey.locked_time),
          currentSurvey.timezone
        ),
        "yyyy-MM-dd",
        { timeZone: currentSurvey.timezone }
      )}`}
      intent={Intent.DANGER}
      disabled
    />
  );

  return (
    <>
      <ButtonGroup>
        {locked ? (
          isArchived ? (
            lockedOnButton
          ) : (
            <CheckViewAccess fallback={lockedOnButton}>
              <Button
                icon="lock"
                text={`Locked on ${format(
                  utcToZonedTime(
                    new Date(currentSurvey.locked_time),
                    currentSurvey.timezone
                  ),
                  "yyyy-MM-dd",
                  { timeZone: currentSurvey.timezone }
                )}`}
                intent={Intent.DANGER}
                onClick={() => setShowLockedPopup(true)}
              />
            </CheckViewAccess>
          )
        ) : showLock && !isArchived ? (
          <CheckEditAccess
            fallback={
              <Button
                icon="lock"
                text="Lock Survey and Generate Reports"
                intent={Intent.PRIMARY}
                disabled
              />
            }
          >
            <Button
              icon="lock"
              text="Lock Survey and Generate Reports"
              intent={Intent.PRIMARY}
              onClick={() => setShowLockedPopup(true)}
            />
          </CheckEditAccess>
        ) : null}
        <CheckViewAccess fallback={null}>
          <Button
            icon="folder-close"
            text={`Downloads (${downloadsForSurvey.length})`}
            intent="primary"
            loading={isLoading}
            onClick={() => setShowDownloadsPopup(true)}
          />
          <Button
            icon="th"
            text="Spreadsheet Preview"
            intent="primary"
            onClick={() => navigate(`/surveys/${id}/report`)}
          />
        </CheckViewAccess>
        <DownloadSelector
          downloads={downloadsForSurvey}
          isOpen={showDownloadsPopup}
          onClose={() => setShowDownloadsPopup(false)}
          variant="dialog"
          title={`${downloadsForSurvey.length} Downloads`}
          isLoading={isLoading}
        />
        {locked ? (
          <Alert
            className="bp6-dark"
            confirmButtonText={hasEditAccess ? "Unlock Survey" : "Ok"}
            cancelButtonText="Cancel"
            icon="lock"
            intent={hasEditAccess ? Intent.DANGER : Intent.PRIMARY}
            loading={lockingInProgress}
            isOpen={showLockedPopup}
            onConfirm={() => {
              if (hasEditAccess === false) {
                setShowLockedPopup(false);
                return;
              }
              setLockingInProgress(true);
              confirmUnlockSurvey({
                unlock: () => ApiSurveys.unlockSurvey(currentSurvey.id),
                resink,
                onError: showError,
              })
                .then((ok) => {
                  if (ok) setShowLockedPopup(false);
                })
                .finally(() => setLockingInProgress(false));
            }}
            onCancel={() => setShowLockedPopup(false)}
            style={{ maxWidth: "768px", width: "768px" }}
          >
            <p
              css={css`
                margin-bottom: 1rem;
              `}
            >
              This survey was locked on{" "}
              {format(
                utcToZonedTime(
                  new Date(currentSurvey.locked_time),
                  currentSurvey.timezone
                ),
                "MMMM do, yyyy",
                { timeZone: currentSurvey.timezone }
              )}{" "}
              at{" "}
              {format(
                utcToZonedTime(
                  new Date(currentSurvey.locked_time),
                  currentSurvey.timezone
                ),
                "HH:mm:ss zzz",
                { timeZone: currentSurvey.timezone }
              )}{" "}
              by {currentSurvey.locked_by_name}.
            </p>
            {hasEditAccess ? (
              <>
                <p
                  css={css`
                    margin-bottom: 1rem;
                  `}
                >
                  Note that unlocking a survey and making changes will create a
                  new version of the spreadsheet and PDF reports. It may take
                  several minutes to regenerate the reports.
                </p>
                <p
                  css={css`
                    margin-bottom: 1rem;
                  `}
                >
                  All survey lock and unlock events are logged and email
                  notifications are sent to the folder members who subscribe to
                  them.
                </p>
              </>
            ) : null}
          </Alert>
        ) : (
          <Alert
            className="bp6-dark"
            cancelButtonText="Cancel & Review"
            confirmButtonText="Confirm & Lock"
            icon="lock"
            intent={Intent.DANGER}
            isOpen={showLockedPopup}
            loading={lockingInProgress}
            onCancel={() => setShowLockedPopup(false)}
            onConfirm={() => {
              setLockingInProgress(true);
              confirmLockSurvey({
                lock: () => ApiSurveys.lockSurvey(currentSurvey.id),
                generateReport:
                  generateReport === false
                    ? null
                    : () => ApiSurveys.generateSurveyReport(currentSurvey.id),
                resink,
                onError: showError,
              })
                .then((ok) => {
                  if (ok) setShowLockedPopup(false);
                })
                .finally(() => setLockingInProgress(false));
            }}
            style={{ maxWidth: "768px", width: "768px" }}
          >
            <p>
              Locking this survey will prevent further edits of the asset
              measurements and will trigger the generation of the spreadsheet
              and PDF reports that can be found in the “Downloads” tab once
              produced. Please ensure the data is correct and confirm the
              action.
            </p>
          </Alert>
        )}
      </ButtonGroup>
    </>
  );
};

const Footer = ({ survey, orders }) => {
  const match = useMatch("/surveys/:id");
  const isArchived = isSurveyArchived(survey);

  if (match) {
    return (
      <Navbar>
        <NavbarGroup>
          <Orders
            status={survey && survey.status}
            orders={orders}
            isArchived={isArchived}
          />
        </NavbarGroup>
        <NavbarGroup align={Alignment.RIGHT}>
          <Downloads />
        </NavbarGroup>
      </Navbar>
    );
  }

  return null;
};

export default Footer;
